import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox,
  IconButton, TablePagination, alpha, CircularProgress, Alert, Menu, MenuItem, Snackbar,
  useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, LayoutGrid } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminCategories, deleteAdminCategory, bulkDeleteAdminCategories } from '../redux/slices/catalogSlice';
import CategoryModal from '../components/CategoryModal';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import axios from '../utils/axios';
import ExportTableButton from '../components/common/ExportTableButton';

const CategoriesView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: categories, total, loading, error } = useSelector((state) => state.catalog.categories);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Action Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionCategory, setActionCategory] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce the search box. Only reset to page 1 when the text actually
  // changed — this used to fire 500 ms after every mount and bounce an admin
  // who had already clicked to page 2 back to page 1.
  useEffect(() => {
    if (searchTerm === debouncedSearch) return undefined;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);


  const loadCategories = useCallback(() => {
    dispatch(fetchAdminCategories({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch]);

  // Deleting the last row(s) of the last page left an empty page; step back.
  useEffect(() => {
    if (!loading && page > 0 && categories.length === 0 && total > 0) {
      setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));
    }
  }, [loading, page, categories.length, total, rowsPerPage]);

  // After CREATING, show page 1 with no search: the list is newest-first, so
  // the new item is there. Reloading the current page (page 3, or a search)
  // made a just-created item look "not found".
  const showNewest = useCallback(() => {
    if (page === 0 && !searchTerm && !debouncedSearch) loadCategories();
    else { setSearchTerm(''); setDebouncedSearch(''); setPage(0); }
  }, [page, searchTerm, debouncedSearch, loadCategories]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = categories.map((n) => n._id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const handleMenuOpen = (event, category) => {
    setAnchorEl(event.currentTarget);
    setActionCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActionCategory(null);
  };

  const handleEditClick = () => {
    setEditingCategory(actionCategory);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setAnchorEl(null);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        const result = await dispatch(bulkDeleteAdminCategories(selectedIds));
        if (result.meta.requestStatus === 'fulfilled') {
          setSelectedIds([]);
          setSnackbar({ open: true, message: 'Selected categories deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete categories', severity: 'error' });
        }
      } else if (actionCategory) {
        const result = await dispatch(deleteAdminCategory(actionCategory._id));
        if (result.meta.requestStatus === 'fulfilled') {
          setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete category', severity: 'error' });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setActionCategory(null);
      loadCategories();
    }
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  return (
    <Box>
      <PageHeader
        title="Categories"
        subtitle="Manage primary product and business classification taxonomy"
        count={total}
        countLabel="categories"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => { setEditingCategory(null); setIsModalOpen(true); }}
          >
            New category
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search category..."
          selectedCount={selectedIds.length}
          onBulkDelete={handleBulkDeleteClick}
          hasActiveFilters={Boolean(searchTerm)}
          onResetFilters={() => { setSearchTerm(''); setPage(0); }}
        >
          <ExportTableButton
            data={categories}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/admin/categories', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                },
              });
              return res.data.categories || [];
            }}
            columns={[
              { label: 'Category Name', key: 'category_name' },
              { label: 'Description', key: 'category_description' },
              { label: 'Status', key: 'category_status' },
            ]}
            filename="categories"
          />
        </FilterBar>

        {error && !loading ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 480, sm: 600 } }}>
              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={selectedIds.length > 0 && selectedIds.length < categories.length}
                      checked={categories.length > 0 && selectedIds.length === categories.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Category Name</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length > 0 ? categories.map((cat) => {
                  const isItemSelected = isSelected(cat._id);
                  return (
                    <TableRow key={cat._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, cat._id)}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{cat.category_name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{cat.category_description || 'No description'}</TableCell>
                      <TableCell>
                        <StatusBadge
                          status={cat.category_status === 'Activated' ? 'active' : 'inactive'}
                          label={cat.category_status}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, cat)}>
                          <MoreVertical size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }) : !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ p: 0 }}>
                      <EmptyState
                        icon={LayoutGrid}
                        title="No categories found"
                        description="Try adjusting your search query or create a new category."
                        actionLabel="Add Category"
                        onAction={() => { setEditingCategory(null); setIsModalOpen(true); }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 140, borderRadius: 2 } }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal */}
      <CategoryModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        category={editingCategory}
        onSuccess={() => (editingCategory ? loadCategories() : showNewest())}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        content={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedIds.length} categories? This action cannot be undone.`
            : `Are you sure you want to delete "${actionCategory?.category_name || 'this category'}"? This action cannot be undone.`
        }
        confirmText="Delete"
        severity="error"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoriesView;
